import React, { useEffect, useState, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import {
    Card,
    CardBody,
    Table,
    MenuSVG,
    HeaderTitle,
} from '../../../../components';
import { FilterQueryParams } from '../../../../helpers';
import { downloadCSV } from '../../../../helpers/download';
import { usePaymentLinksUIContext } from '../context/PaymentLinksUIContext';
import PaymentLinksFilters from './PaymentLinksFilters';
import page from '../../../../constants/pages/page';
import { usersService, reportsService } from '../../../../services';
import { UIHelpers } from '../../../../constants/pages/UIHelpers';
import paymentLinksColumns from './PaymentLinksColumns';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import moment from 'moment';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { checkFileStatus } from '../../../../helpers/fibonacciHelper';
import CustomAlert from '../../../../components/Alert';

function PaymentLinksTable () {
    const { t } = useTranslation();
    const { id } = useParams();
    const linkId = parseInt(id);

    const paymentLinksFiltersRef = useRef();
    const customSortedKeyMap = { 'status.description': 'id_payment_link_status' };
    const { sortedKeyMap, defaultSorted } = UIHelpers(customSortedKeyMap);
    // State

    // Context State
    const paymentLinksUIContext = usePaymentLinksUIContext();
    const paymentLinksUIProps = useMemo(() => {
        return {
            isLoading: paymentLinksUIContext.isLoading,
            setIsLoading: paymentLinksUIContext.setIsLoading,
            queryParams: paymentLinksUIContext.queryParams,
            setQueryParams: paymentLinksUIContext.setQueryParams,
            paymentLinks: paymentLinksUIContext.paymentLinks,
            setPaymentLinks: paymentLinksUIContext.setPaymentLinks,
        };
    }, [paymentLinksUIContext]);

    // Alert states
    const [alert, setAlert] = useState({
        severity: '',
        title: '',
        description: '',
        className: '',
    });
    const [openAlert, setOpenAlert] = useState(false);
    const [totalPayments, setTotalPayments] = useState(0);

    // Functions
    const fetchLink = async (id) => {
    // change state isLoading
        paymentLinksUIProps.setIsLoading(true);
        // prepare options (order, pages and filters)
        const options = {
            filters: `id eq ${id}`
        };

        try {
            const paymentsLinks = await usersService.getPaymentLinks(options);
            paymentLinksUIProps.setPaymentLinks(paymentsLinks);
            setTotalPayments(paymentsLinks.length);
            paymentLinksUIProps.setIsLoading(false);
        }
        catch (error) {
            paymentLinksUIProps.setPaymentLinks([]);
            setTotalPayments(0);
            setAlert((prevAlert) => ({
                ...prevAlert,
                severity: 'error',
                title: t('errors.generalTitle'),
                description: t('errors.generalDescription'),
                className: 'clientsMC_Alert--error',
            }));
            setOpenAlert(true);
            paymentLinksUIProps.setIsLoading(false);
        }
    };
    const fetchPayments = async () => {
    // change state isLoading
        paymentLinksUIProps.setIsLoading(true);
        // prepare options (order, pages and filters)
        const filter =
      paymentLinksUIProps.queryParams.filters || new FilterQueryParams();
        const { sortField, sortOrder } = paymentLinksUIProps.queryParams;
        const options = {
            order: `${sortedKeyMap[sortField] ||
        sortField}-${sortOrder.toUpperCase()}`,
            pageSize: paymentLinksUIProps.queryParams.pageSize,
            page: paymentLinksUIProps.queryParams.pageNumber,
        };

        // Set filters on options.
        options.filters = filter.build();

        try {
            const paymentsLinks = await usersService.getPaymentLinks(options);
            paymentLinksUIProps.setPaymentLinks(paymentsLinks);
            paymentLinksUIProps.setIsLoading(false);
        }
        catch (error) {
            paymentLinksUIProps.setPaymentLinks([]);
            setAlert((prevAlert) => ({
                ...prevAlert,
                severity: 'error',
                title: t('errors.generalTitle'),
                description: t('errors.generalDescription'),
                className: 'clientsMC_Alert--error',
            }));
            setOpenAlert(true);
            paymentLinksUIProps.setIsLoading(false);
        }
    };

    const getTotalLinks = async () => {
        paymentLinksUIProps.setIsLoading(true);
        try {
            const filters =
        paymentLinksUIProps.queryParams.filters || new FilterQueryParams();
            const options = {
                unionPeople: true,
                pageSize: 1000000,
            };
            options.filters = filters.build();
            const paymentsLinksTotal = await usersService.getPaymentLinks(options);
            setTotalPayments(paymentsLinksTotal.length);
            paymentLinksUIProps.setIsLoading(false);
        }
        catch (error) {
            paymentLinksUIProps.setIsLoading(false);
            setAlert((prevAlert) => ({
                ...prevAlert,
                severity: 'error',
                title: t('errors.generalTitle'),
                description: t('errors.generalDescription'),
                className: 'clientsMC_Alert--error',
            }));
            setOpenAlert(true);
        }
    };

    const getData = async () => {
        if (linkId) {
            fetchLink(linkId);
        }
        else {
            await getTotalLinks();
            fetchPayments();
        }
    };

    // Handle query params changes. On any param change, fetch users.
    useEffect(
        () =>
            paymentLinksUIProps.setQueryParams((prev) => ({
                ...prev,
                pageSize: localStorage.getItem('sizePerPage') || page.pageSize,
            }))
        ,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []);
    useEffect(() => {
        if (JSON.stringify(paymentLinksUIProps.queryParams.url) !== undefined) {
            getData();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentLinksUIProps.queryParams]);

    const resetFilters = () => {
        if (paymentLinksFiltersRef.current) {
            paymentLinksFiltersRef.current.reset();
        }
    };

    const getDownloadFilters = () => {
        if (!JSON.stringify(paymentLinksUIProps?.queryParams?.filters?.filters)) { return ''; }
        const filters = { ...paymentLinksUIProps?.queryParams?.filters?.filters };
        delete filters.created_at;
        return JSON.stringify(filters)
            .replace(/[{}"]/g, '')
            .replace(/:/g, ' ')
            .replace(replaceCommaPattern, replaceComma);
    };
    const replaceCommaPattern = /\[([^,]+),([^,\]]+)\]/;
    const replaceComma = (match, p1, p2) => {
        return `[${p1};${p2}]`;
    };
    const download = async () => {
        paymentLinksUIProps.setIsLoading(true);

        const fetchFile = (idFile) => {
            return checkFileStatus({ filters: `id eq ${idFile}` }, 60, 3);
        };
        const startDate =
      paymentLinksUIProps.queryParams?.filters?.filters?.created_at?.be[0] ||
      moment()
          .subtract(730, 'days')
          .format('YYYY-MM-DD HH:mm:ss');
        const endDate =
      paymentLinksUIProps.queryParams?.filters?.filters?.created_at?.be[1] ||
      moment().utc().format('YYYY-MM-DD HH:mm:ss');
        const filters =
      (paymentLinksUIProps.queryParams?.filters?.filters?.created_at?.be[0]
          ? `created_at be [${startDate};${endDate}]`
          : '') +
      (paymentLinksUIProps.queryParams?.filters?.filters?.created_at?.be[0] &&
      getDownloadFilters()
          ? `,${getDownloadFilters()}`
          : getDownloadFilters() || '');
        const options = {
            idType: 30, // id report of payment links table
            startAt: startDate,
            endAt: endDate,
            metadata: {
                filters: filters,
                order: 'id-DESC',
            },
        };
        const nameTable = window.location.pathname.slice(
            1,
            window.location.pathname.length
        );
        const fileName = startDate
            ? `Reporte del ${options.startAt} al ${moment().format('YYYY-MM-DD HH:mm:ss')} ${nameTable}.csv`
            : `Reporte ${nameTable}.csv`;
        await reportsService
            .createReport(options)
            .then(async (response) => {
                return await fetchFile(response.metadata.id_file);
            })
            .then(async (res) => {
                return await axios.get(res[0].description, {
                    responseType: 'arraybuffer',
                });
            })
            .then((response) => {
                downloadCSV(response.data, fileName);
                paymentLinksUIProps.setIsLoading(false);
            })
            .catch(() => {
                paymentLinksUIProps.setIsLoading(false);
                setAlert((prevAlert) => ({
                    ...prevAlert,
                    severity: 'error',
                    title: t('errors.generalTitle'),
                    description: t('errors.downloadFile'),
                    className: 'clientsMC_Alert--error',
                }));
                setOpenAlert(true);
            });
    };

    // Main Component

    return (
        <>
            <CustomAlert
                openAlert={openAlert}
                setOpenAlert={setOpenAlert}
                alert={alert}
                setAlert={setAlert}
            />
            <Card>
                <Box>
                    <HeaderTitle
                        title={t('paymentLinks.table.title')}
                        total={totalPayments}
                        reset={resetFilters}
                        icon={<MenuSVG className="mr-1 mb-2" />}
                        download={download}
                        UIProps={paymentLinksUIProps}
                        margBottom={'6px'}
                    />
                    <PaymentLinksFilters ref={paymentLinksFiltersRef} />
                </Box>
                <CardBody>
                    <Table
                        data={paymentLinksUIProps.paymentLinks}
                        columns={paymentLinksColumns()}
                        sort={defaultSorted}
                        onChange={paymentLinksUIProps.setQueryParams}
                        isLoading={paymentLinksUIProps.isLoading}
                        total={totalPayments}
                        pagination={{
                            sizePerPage: paymentLinksUIProps.queryParams.pageSize,
                            page: paymentLinksUIProps.queryParams.pageNumber,
                        }}
                    />
                </CardBody>
            </Card>
        </>
    );
}

const mapStateToProps = ({ auth: { user } }) => ({
    user,
});

export default connect(mapStateToProps)(PaymentLinksTable);
